import { useState, type ReactNode } from 'react'
import { Icon, type IconName } from '../brand/Icon'
import { HintDot } from '../help/HintDot'
import { useT } from '../i18n'
import { nextAccordion } from './accordionState'

// 'forecast' упразднён (2026-08-03): слит с 'hsi' — оба рисовали один и тот же
// probability-слой. Старые ссылки `model=forecast` парсер маппит в 'hsi'.
export type ModelKind = 'hsi' | 'upwelling' | 'currents'

export const MODELS: { id: ModelKind; labelKey: string; icon: IconName }[] = [
  { id: 'currents', labelKey: 'models.kind.currents', icon: 'ic-currents' },
  { id: 'upwelling', labelKey: 'models.kind.upwelling', icon: 'ic-upwelling' },
  // «Комфортная среда» вместо «HSI по видам» (решение владельца 2026-08-07):
  // меняется ТОЛЬКО подпись, модель и шкала прежние. Подпись остаётся видовой
  // по смыслу — индекс считается по выбранному виду, и обещать видонезависимость
  // названием нельзя.
  { id: 'hsi', labelKey: 'models.kind.hsi', icon: 'ic-hsi' },
]

interface ModelAccordionProps {
  active: ModelKind
  onSelect: (m: ModelKind) => void
  /** параметры дочернего уровня активной модели (вид/дата/глубина/флот) */
  body: (m: ModelKind) => ReactNode
  /** Доступен ли раздел домену (`sectionRules.sectionAllowed`); недоступный
   *  не рисуется вовсе. Без пропа — все разделы. */
  allowed?: (m: ModelKind) => boolean
}

/** Гармошка моделей: модель — заголовок верхнего уровня; выбор её же разворачивает
 *  дочерние настройки, остальные сворачиваются. Один жест = выбор + раскрытие. */
export function ModelAccordion({ active, onSelect, body, allowed }: ModelAccordionProps) {
  // Свёрнутость — состояние ПАНЕЛИ, а не выбора: свёрнутый раздел остаётся
  // выбранным, и слой с карты не уходит (см. accordionState).
  const [collapsed, setCollapsed] = useState(false)
  const t = useT()
  const click = (id: ModelKind) => {
    const next = nextAccordion(active, collapsed, id)
    setCollapsed(next.collapsed)
    if (next.active !== active) onSelect(next.active)
  }
  return (
    <div>
      <div className="eyebrow mb-3 flex items-center gap-1.5">
        {t('models.pickerTitle')}
        <HintDot id="model" />
      </div>
      <div className="flex flex-col gap-1.5">
        {MODELS.filter(m => !allowed || allowed(m.id)).map(m => {
          const open = m.id === active && !collapsed
          return (
            <div
              key={m.id}
              className={
                m.id === active
                  ? 'overflow-hidden rounded-xl bg-trench/50 shadow-[inset_0_0_0_1px_rgb(var(--accent))]'
                  : ''
              }
            >
              <button
                type="button"
                onClick={() => click(m.id)}
                aria-expanded={open}
                className={[
                  'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left text-sm transition-colors',
                  open ? 'text-foam' : 'bg-trench text-mist hover:text-foam',
                ].join(' ')}
              >
                <Icon
                  name={m.icon}
                  className={['h-4 w-4 shrink-0', open ? 'text-sonar' : 'text-mist'].join(' ')}
                />
                <span className="flex-1 leading-tight">{t(m.labelKey)}</span>
                <svg
                  viewBox="0 0 16 16"
                  className={[
                    'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                    open ? 'rotate-90 text-sonar' : 'text-faint',
                  ].join(' ')}
                  aria-hidden
                >
                  <path
                    d="M6 4l4 4-4 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {open && (
                <div className="animate-[accordion-in_0.2s_ease] space-y-5 px-3 pb-4 pt-1.5">
                  {body(m.id)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
